import messages from '../../constants/logMessages.json';
import tracking from '../../tracking/tracking';
import {
  AnalyticsConst,
  AnalyticsEventTypes,
  AnalyticsPageView,
  BuyStart,
  EcommercePurchasePayload,
  IndicativeQuote,
  Purchase,
  QuoteComplete,
  QuoteStart,
} from '../../types';
import logging, { getMessage } from '../logging/index';
import tools from '../tools/index';
import {
  completeTimeHandler,
  CompletionEvents,
} from './startCompleteTimeHelper';

const { infoHeading, warnHeading, infoBold, warnBold } = messages;

type Milestones =
  | QuoteStart
  | QuoteComplete
  | BuyStart
  | Purchase
  | IndicativeQuote;

// A function to perform runtime checks to determine if the provided object
// matches the structure of an EcommercePurchasePayload.
const isEcommercePurchasePayload = (
  obj: any
): obj is EcommercePurchasePayload =>
  obj &&
  typeof obj.currency === 'string' &&
  typeof obj.value === 'number' &&
  typeof obj.transaction_id === 'string' &&
  Array.isArray(obj.items);

/**
 * Manages the validation and setup of milestones, checking if a milestone has been previously fired,
 * and resetting the milestone flag if needed.
 *
 * This function serves as a milestone helper and may be invoked directly from the analytics connector file.
 *
 * @param {object} payload - Payload containing information about the event.
 * @param {string} payload.event - The type of event being triggered (e.g., quote_start, buy_start).
 * @returns {boolean|undefined} - Returns true if the milestone is successfully set,
 * false if the milestone has already been fired, and undefined if the payload is empty.
 */
export const milestoneHelperHandler = (
  payload: Milestones
): boolean | undefined => {
  if (!payload) return false;
  const { event = '' } = payload;

  const analyticsMilestones =
    tools.handlePersistedConfig()?.analyticsMilestones || {};

  const eventMapping: Record<string, string> = {
    quote_start: 'quoteStart',
    quote_complete: 'quoteComplete',
    indicative_quote: 'indicativeQuote',
    buy_start: 'buyStart',
    purchase: 'purchase',
  };

  const milestoneKey = eventMapping[event.toLowerCase()];

  // Dedupe Logic - Don't fire if milestone already fired
  if (milestoneKey && analyticsMilestones[milestoneKey]) {
    logging.info(getMessage('alreadyFired', [event]), infoHeading, infoBold);
    return false;
  }

  tools.setMilestone(milestoneKey);
  return true;
};

/**
 * Process a milestone event by preparing the payload and performing necessary actions.
 *
 * @param {string} event - The name or identifier of the event.
 * @param {string} eventAction - The action associated with the event.
 * @param {string} eventLabel - A label describing the event.
 * @param {Record<string, any>} data - Additional data associated with the event.
 * @returns {false | undefined} - Returns `false` if milestone handling fails, otherwise returns `undefined`.
 */
const processMilestoneEvent = (
  event: string, // Use string type here
  eventAction: string,
  eventLabel: string,
  data: Record<string, any>
): false | undefined => {
  const newPayload = {
    event,
    eventAction,
    eventLabel,
    data: {
      ...data,
    },
  };
  delete newPayload.data.indicativeQuote;
  delete newPayload.data.quoteComplete;
  delete newPayload.data.buyStart;

  // Checking whether milestone has previously fired, and also manage the registering the milestone flag.
  if (!milestoneHelperHandler(newPayload as Milestones)) return false;

  tracking.analyticsCustomEvent(
    completeTimeHandler(newPayload as CompletionEvents)
  );
  return undefined;
};

/**
 * Handles the processing of Indicative Quote and Quote Complete milestone events.
 *
 * @param {AnalyticsEventTypes} eventType - The type of analytics event.
 * @param {string} eventName - The name of the event.
 * @param {AnalyticsPageView} payload - The payload containing information about the event.
 * @returns {boolean} - Returns true if the milestone event is handled successfully, otherwise false.
 */
const handleQuoteMilestoneEvent = (
  eventType: AnalyticsEventTypes,
  eventName: string,
  payload: AnalyticsPageView
) => {
  const { data } = payload;
  const { quoteNumber = '' } = data;
  // All quote events need to have quoteNumber except indicative quote
  if (
    (eventName !== 'indicative quote' && !quoteNumber) || // quoteNumber is falsy
    (eventName !== 'indicative quote' && typeof quoteNumber !== 'string') // quoteNumber is not a string
  ) {
    logging.info(
      getMessage('noQuoteNumber', [eventType]),
      warnHeading,
      warnBold
    );
    return false;
  }
  // Condition pass - heading to processMilestoneEvent
  return processMilestoneEvent(eventType, eventName, quoteNumber, data);
};

/**
 * Handles the processing of a purchase milestone event, including validations and tracking.
 *
 * @param buyStartFlag - Flag indicating if the buy start event was fired.
 * @param quoteNumber - The quote number associated with the purchase.
 * @param policyId - The policy ID associated with the purchase.
 * @param policyNumber - The policy number associated with the purchase.
 * @param payload - The payload containing information about the purchase event.
 * @returns Returns true if the purchase event is handled successfully, false otherwise.
 */
const handlePurchaseMilestoneEvent = (
  buyStartFlag: boolean,
  quoteNumber: string,
  policyId: string,
  policyNumber: string,
  payload: AnalyticsPageView
): boolean => {
  // Warn Logic - Log if no buy_start event was fired previously. Do not return early.
  if (!buyStartFlag) {
    logging.info(
      getMessage('noBuyStart', [AnalyticsEventTypes.BuyStart]),
      warnHeading,
      warnBold
    );
  }

  // Dedupe Logic - If quoteNumber or policyId don't exist then return early.
  const policyIdentification = policyId || policyNumber;
  if (
    !quoteNumber ||
    typeof quoteNumber !== 'string' ||
    !policyIdentification ||
    typeof policyIdentification !== 'string'
  ) {
    const noPurchaseQuoteOrPolicyMessage = getMessage(
      'noPurchaseQuoteOrPolicy',
      [AnalyticsEventTypes.Purchase]
    );
    logging.info(noPurchaseQuoteOrPolicyMessage, warnHeading, warnBold);
    tracking.analyticsCustomEvent({
      event: 'error_milestone',
      eventAction: noPurchaseQuoteOrPolicyMessage.replace(/%c/g, ''),
      eventLabel: `quoteId: ${quoteNumber}|policyId: ${policyIdentification}`,
    });
    return false;
  }

  // Extract ecommerce properties and the rest of the payload
  const {
    ecommerce = {},
    // virtualPagePath,
    ...payloadWithoutEcommercePagePath
  } = payload;

  // Combine the extracted properties to form the new payload
  const newPayload: {
    [key: string]: any;
    ecommerce: any;
    // Add other properties as needed
  } = {
    ...payloadWithoutEcommercePagePath,
    ecommerce,
  };

  // Dedupe Logic - check the ecommerce payload against some strong types and return early if failed.
  if (!isEcommercePurchasePayload(ecommerce)) {
    const noEcommerceObjectMessage = getMessage('noEcommerceObject', [
      AnalyticsEventTypes.Purchase,
    ]);
    logging.info(noEcommerceObjectMessage, warnHeading, warnBold);
    tracking.analyticsCustomEvent({
      event: 'error_milestone',
      eventAction: noEcommerceObjectMessage.replace(/%c/g, ''),
      eventLabel:
        ecommerce && typeof ecommerce === 'object'
          ? JSON.stringify(ecommerce)
          : 'ecommerce object undefined',
    });
    return false;
  }

  // Prepare the ecommerce payload for tracking
  const ecommercePayload = {
    event: AnalyticsEventTypes.Purchase,
    eventAction: 'purchase',
    eventLabel: policyId || AnalyticsConst.NOT_SET,
    ...newPayload,
    data: {
      ...newPayload.data,
    },
  };

  // If milestone handling fails, return early.
  if (!milestoneHelperHandler(ecommercePayload as Milestones)) {
    return false;
  }

  // Remove the virtualPagePath key from the ecommercePayload
  // if (ecommercePayload.virtualPagePath) delete ecommercePayload.virtualPagePath;

  // Perform site tracking for ecommerce purchase event
  const ecommercePayloadWithTime = completeTimeHandler(
    ecommercePayload as unknown as Purchase
  );

  // Clean up data points
  delete ecommercePayloadWithTime.data.appComplete;
  delete ecommercePayloadWithTime.data.purchase;

  tracking.analyticsEcommercePurchase(ecommercePayloadWithTime as Purchase);
  return true;
};

/**
 * The `kpiMilestoneHandler` function processes milestone events triggered by data point flags
 * in a page view event payload. It checks specific flags in the payload to determine which milestone
 * events should be fired and prepares the payload accordingly.
 *
 * @param payload - The AnalyticsPageView payload containing data points for the page view event.
 * @returns `true` if a milestone event was successfully processed, otherwise `false`.
 */
export const kpiMilestoneHandler = (payload: AnalyticsPageView) => {
  const { data = {} } = payload;
  const {
    quoteComplete = false,
    indicativeQuote = false,
    purchase = false,
    quoteNumber = '',
    policyId = '',
    policyNumber = '',
  } = data;
  const analyticsMilestones =
    tools.handlePersistedConfig()?.analyticsMilestones || {};

  const quoteStartFlag = analyticsMilestones.quoteStart ?? false;
  const buyStartFlag = analyticsMilestones.buyStart ?? false;

  /*
   * Handle Quote Complete or indicative quote, all these event types will go to the handleQuoteMilestoneEvent function.
   */
  // Check and delete timeAppCompletion if it exists fired, and avoid firing it again
  if (data?.timeAppCompletion) {
    delete data.timeAppCompletion;
  }

  if (quoteComplete) {
    data.cm_quote_complete = 1;
  }

  if (indicativeQuote) {
    data.cm_indicative_quote = 1;
  }

  if (purchase) {
    data.cm_purchase = 1;
  }

  /*
   * Handle Quote Complete or indicative quote, all these event types will go to the handleQuoteMilestoneEvent function.
   */
  if (quoteComplete || indicativeQuote) {
    const eventType = quoteComplete
      ? AnalyticsEventTypes.QuoteComplete
      : AnalyticsEventTypes.IndicativeQuote;
    const eventName = quoteComplete ? 'quote complete' : 'indicative quote';

    // TODO: Exclude retrieve quote
    // Warning Logic: Check for previously detected milestones and issue warnings if not detected.
    if (!quoteStartFlag && (quoteComplete || indicativeQuote)) {
      logging.info(
        getMessage('noQuoteStart', [eventType]),
        warnHeading,
        warnBold
      );
      // Terminate due to quote start flag is not set.
      return false;
    }
    handleQuoteMilestoneEvent(eventType, eventName, payload);
  }

  /*
   * Handle Purchase
   */
  // If not purchase event return early.
  if (!purchase) {
    return false;
  }
  return handlePurchaseMilestoneEvent(
    buyStartFlag,
    quoteNumber,
    policyId,
    policyNumber,
    payload
  );
};
